import { memo } from "react";
import { Icon } from "@iconify/react";
import { Skeleton } from "antd";
import { InfiniteScrollContainer } from "@/shared/components/InfiniteScrollContainer";
import { cn } from "@/shared/lib/utils";
import type { UploadJobStatus, ZipContentFile, ParsingIssue } from "@/features/patient/records/api/dataPipelineApi";
import { useSmoothedUploadProgress } from "@/features/patient/records/hooks/useSmoothedUploadProgress";
import { listCardClass } from "@/shared/lib/cardStyles";

// "uploading" is a frontend-only value — the moment before the POST to
// /upload has even resolved, before the backend has a row to report a real
// stage for at all (see UploadRecords.tsx's client-side ramp). Every other
// value comes straight from the backend's own aggregate computation (see
// get_upload_processing_progress) — never guessed or timed on this end.
// "ocr" / "ocr_converting": pages that needed OCR (scanned, or a broken font)
// being read by their own OCR job after the rest, then read by the AI.
export type UploadProcessingStage =
  "uploading" | "queued" | "extracting" | "converting" | "ocr" | "ocr_converting" | "processing";

export interface ZipUploadEntry {
  id: string;
  uploadId?: string;
  fileName: string;
  // Patient-entered label from the Upload Records modal's "Title" field —
  // undefined wherever not given; every display site falls back to
  // fileName, same convention as imaging_studies.title.
  title?: string;
  uploadedAt: number;
  status: UploadJobStatus;
  errorMessage?: string;
  fileCount?: number;
  files?: ZipContentFile[];
  parsingIssues?: ParsingIssue[];
  // TEMP-TIMING: see dataPipelineApi.ts's UploadStatusResponse.processing_seconds.
  processingSeconds?: number;
  // Both only meaningful while status === "processing" — see
  // UploadProcessingStage's own docstring above for where each value comes from.
  processingStage?: UploadProcessingStage;
  // Stage "ocr": pages read by OCR so far, out of the pages that need it.
  processingOcrDone?: number;
  processingOcrTotal?: number;
  processingProgress?: number;
  // Which tab UploadDetailPanel opens to for this entry, set once at
  // creation (see UploadRecords.tsx's handleUpload) and never changed
  // afterward. undefined means the ordinary default (Issues) — set to
  // "pdf" only when this upload was a single, plain PDF file, so landing
  // on "Your Uploads" right after submitting one goes straight to its
  // parsed data instead of an Issues tab that's almost always empty for a
  // single clean PDF. A zip, a multi-file batch, or any non-PDF upload
  // keeps the ordinary Issues default — those are exactly the uploads
  // where Issues is actually likely to have something worth seeing first.
  preferredTab?: "issues" | "structured" | "pdf";
  // Distinguishes a direct-API sync entry (e.g. Epic) from a real file
  // upload — undefined/"upload" is the ordinary case. A "sync" entry has no
  // file/zip contents (files/parsingIssues are seeded to [] at creation, not
  // left undefined, specifically so UploadHistoryView's lazy-fetch effect
  // never calls getUploadStatus for it — see useEpicConnectReturn.ts) and no
  // "View Original File"/"View PDF Data" content, but otherwise renders
  // through this same row/progress-bar/detail-panel UI unchanged.
  kind?: "upload" | "sync";
  // Which direct-API provider this sync came from ("epic" today) — only
  // meaningful when kind === "sync". Plain string, not an enum, so a future
  // provider needs no type change here.
  provider?: string;
}

const PROCESSING_STAGE_LABEL: Record<UploadProcessingStage, string> = {
  uploading: "Uploading…",
  queued: "Preparing…",
  extracting: "Reading your document…",
  converting: "Analyzing with AI…",
  ocr: "Reading scanned pages…",
  ocr_converting: "Adding records from scanned pages…",
  processing: "Processing…",
};

/** The row's stage text; the OCR stage says how far it has got. */
export function processingStageLabel(entry: Pick<ZipUploadEntry, "processingStage" | "processingOcrDone" | "processingOcrTotal">): string {
  const stage = entry.processingStage ?? "processing";
  if (stage === "ocr" && entry.processingOcrTotal) {
    return `Reading scanned pages: ${entry.processingOcrDone ?? 0} of ${entry.processingOcrTotal}`;
  }
  return PROCESSING_STAGE_LABEL[stage];
}

// One icon per stage, used by the sidebar row's spinner. UploadToastStack's
// bottom-right cards deliberately don't show an icon at all (text-only), so
// this is no longer shared with that component.
export const PROCESSING_STAGE_ICON: Record<UploadProcessingStage, string> = {
  uploading: "lucide:upload-cloud",
  queued: "lucide:inbox",
  extracting: "lucide:file-search",
  converting: "lucide:sparkles",
  ocr: "lucide:scan-text",
  ocr_converting: "lucide:sparkles",
  processing: "lucide:loader-2",
};

function formatUploadTime(timestamp: number): string {
  return new Date(timestamp).toLocaleString("en-US", {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
}

// TEMP-TIMING: "1:30" style minutes:seconds display for
// ZipUploadEntry.processingSeconds — remove alongside every other
// TEMP-TIMING piece once average/worst-case timing analysis is done.
function formatProcessingDuration(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
}

function monthGroupKey(timestamp: number): string {
  return new Date(timestamp).toLocaleString("en-US", { month: "long", year: "numeric" }).toUpperCase();
}

/** Buckets entries by calendar month/year for display — entries already arrive
 *  newest-first from the API, so each bucket's contents stay in that order too. */
function groupByMonth(entries: ZipUploadEntry[]): { label: string; items: ZipUploadEntry[] }[] {
  const groups: { label: string; items: ZipUploadEntry[] }[] = [];
  const indexByLabel = new Map<string, number>();
  for (const entry of entries) {
    const label = monthGroupKey(entry.uploadedAt);
    let index = indexByLabel.get(label);
    if (index === undefined) {
      index = groups.length;
      indexByLabel.set(label, index);
      groups.push({ label, items: [] });
    }
    groups[index].items.push(entry);
  }
  return groups;
}

const statusIcon: Record<UploadJobStatus, string> = {
  processing: "lucide:loader-2",
  complete: "lucide:check-circle-2",
  failed: "lucide:alert-circle",
  cancelled: "lucide:ban",
};

const statusIconColor: Record<UploadJobStatus, string> = {
  processing: "text-[#DC2626]",
  complete: "text-black",
  failed: "text-[#DC2626]",
  cancelled: "text-[#9CA3AF]",
};

const statusLabelColor: Record<UploadJobStatus, string> = {
  processing: "text-[#DC2626]",
  complete: "text-[#16A34A]",
  failed: "text-[#DC2626]",
  cancelled: "text-[#6B7280]",
};

// Isolated and memoized on purpose: UploadRow re-renders roughly every 200ms
// while processing (the smoothed progress bar's own creep ticker lives in
// the same component — see displayedProgress below), and re-rendering a
// spinning icon that frequently was exactly what caused the "shaking"
// artifact — the icon's own animation kept getting disturbed by unrelated
// re-renders even though its actual props (status, stage, isSelected) hadn't
// changed. Memoizing on those primitive props means this only ever re-renders
// on a genuine stage transition or selection change, leaving animate-spin to
// run completely undisturbed in between.
const RowStatusIcon = memo(function RowStatusIcon({
  status,
  stage,
  isSelected,
}: {
  status: UploadJobStatus;
  stage: UploadProcessingStage | undefined;
  isSelected: boolean;
}) {
  const icon = status === "processing" ? PROCESSING_STAGE_ICON[stage ?? "processing"] : statusIcon[status];
  return (
    <div
      className={cn(
        "mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors",
        isSelected ? "bg-[#F17893]" : "bg-transparent",
      )}
    >
      <Icon
        icon={icon}
        className={cn(
          "h-4 w-4",
          status === "processing" && "animate-spin",
          isSelected ? "text-white" : statusIconColor[status],
        )}
        style={{ transformOrigin: "50% 50%" }}
      />
    </div>
  );
});

function UploadRow({
  entry,
  isSelected,
  onSelect,
}: {
  entry: ZipUploadEntry;
  isSelected: boolean;
  onSelect?: (entry: ZipUploadEntry) => void;
}) {
  // A still-processing upload is selectable too — its records grow page by page
  // as the backend extracts/converts them (see UploadDetailPanel's live poll),
  // so there's real data worth watching before the job reaches "complete".
  const isSelectable = (entry.status === "complete" || entry.status === "processing") && Boolean(onSelect);
  const needsAttention = entry.parsingIssues?.length ?? 0;
  // Smooths the real-but-jumpy backend checkpoints (see the hook's own
  // docstring) instead of rendering entry.processingProgress directly, which
  // used to sit dead still at one number for a long stretch (the "queued"
  // stage in particular can last several seconds) and then jump straight to
  // the next one — this creeps continuously in between instead.
  const displayedProgress = useSmoothedUploadProgress(entry.processingProgress, entry.status === "processing", entry.id);

  return (
    <div
      onClick={isSelectable ? () => onSelect?.(entry) : undefined}
      className={cn(
        "rounded-2xl px-4 py-3",
        listCardClass(isSelected),
        isSelectable && !isSelected && "cursor-pointer",
      )}
    >
      <div className="flex items-start gap-3">
        <RowStatusIcon status={entry.status} stage={entry.processingStage} isSelected={isSelected} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className="truncate text-sm font-extrabold text-[#0A2947]">{entry.title || entry.fileName}</p>
            <span className={cn("shrink-0 text-[11px] font-bold", statusLabelColor[entry.status])}>
              {entry.status === "processing"
                ? processingStageLabel(entry)
                : entry.status === "complete" ? "Complete" : entry.status === "failed" ? "Failed" : "Cancelled"}
            </span>
          </div>
          <p className="mt-0.5 text-xs text-[#9CA3AF]">
            {formatUploadTime(entry.uploadedAt)}
            {/* TEMP-TIMING — remove this whole span (and processingSeconds everywhere) once timing analysis is done */}
            {entry.processingSeconds != null && ` · Processed in ${formatProcessingDuration(entry.processingSeconds)}`}
          </p>
          {needsAttention > 0 && (
            <p className="mt-0.5 text-xs font-bold text-[#F17893]">
              {needsAttention} Need{needsAttention === 1 ? "s" : ""} Attention
            </p>
          )}
          {entry.status === "processing" && (
            <div className="mt-2 flex items-center gap-2">
              <div className="h-1.5 flex-1 rounded-full bg-[#F3F4F6]">
                <div
                  className="h-1.5 rounded-full bg-gradient-to-r from-[#F7B4C3] to-[#F17893] transition-[width] duration-200 ease-linear"
                  style={{ width: `${Math.min(100, Math.max(0, displayedProgress))}%` }}
                />
              </div>
              <span className="shrink-0 text-[11px] font-bold text-[#9CA3AF]">
                {Math.round(Math.min(100, Math.max(0, displayedProgress)))}%
              </span>
            </div>
          )}
          {entry.status === "failed" && entry.errorMessage && (
            <p className="mt-0.5 truncate text-xs text-[#6B7280]">{entry.errorMessage}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export function UploadHistoryList({
  entries,
  selectedId,
  onSelect,
  isLoading = false,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
}: {
  entries: ZipUploadEntry[];
  /** The currently open upload's local id — highlighted per img1. */
  selectedId?: string | null;
  onSelect?: (entry: ZipUploadEntry) => void;
  isLoading?: boolean;
  /** True if more upload history exists past `entries` — drives the scroll-to-load spinner. */
  hasMore?: boolean;
  /** True while a loadMore fetch is in flight — shown as a spinner at the bottom of the list. */
  isLoadingMore?: boolean;
  /** Fetches and appends the next page of upload history, triggered when the list is scrolled near its end. */
  onLoadMore?: () => void;
}) {
  // Only while genuinely nothing is known yet — an entry can already exist
  // here even during the initial hydration fetch (e.g. useEpicConnectReturn's
  // optimistic "Epic Sync" row, added the instant the patient lands back
  // from Epic, before listUploads()/listSyncJobs() have resolved even once).
  // Blindly showing the skeleton for the whole isLoading window used to hide
  // that already-correct entry for a second or two for no reason — render
  // what's actually known instead of waiting on an unrelated fetch.
  if (isLoading && entries.length === 0) {
    return (
      <div className="h-full space-y-3 overflow-hidden" aria-label="Loading upload history" aria-busy="true">
        {[0, 1, 2].map((row) => (
          <div key={row} className="flex items-center gap-3 rounded-2xl border border-[#E5E7EB] bg-white px-4 py-3">
            <Skeleton.Avatar active size={28} shape="circle" />
            <div className="min-w-0 flex-1">
              <Skeleton active title={{ width: "60%" }} paragraph={{ rows: 1, width: "35%" }} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-sm text-[#9CA3AF]">
        <Icon icon="lucide:inbox" className="h-8 w-8 text-[#D1D5DB]" />
        No uploads yet.
      </div>
    );
  }

  const groups = groupByMonth(entries);

  return (
    <InfiniteScrollContainer hasMore={hasMore} isLoadingMore={isLoadingMore} onLoadMore={onLoadMore} className="h-full pr-2">
      <div className="space-y-6 pb-2">
        {groups.map((group) => (
          <div key={group.label}>
            <h3 className="mb-3 text-xs font-extrabold uppercase tracking-wide text-[#9CA3AF]">{group.label}</h3>
            <div className="space-y-3">
              {group.items.map((entry) => (
                <UploadRow key={entry.id} entry={entry} isSelected={selectedId === entry.id} onSelect={onSelect} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </InfiniteScrollContainer>
  );
}
